import classNames from 'classnames';
import React from 'react';

import useDocumentCard from '../../hooks/useDocumentCard';
// import { FormattedMessage } from 'react-intl';
// import useFontSize from '../../hooks/useFontSize';
// import * as AppPropTypes from '../../lib/PropTypes';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import Author from '../partials/Author';
import Category from '../partials/Category';
import DocumentCardThumbnail from '../partials/DocumentCardThumbnail';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import CardTitle from '../typography/CardTitle';
// import SponsorContent from '../typography/SponsorContent';
import OverCard from './OverCard';

import styles from '../../styles/cards/document-over-card.module.css';

export interface DocumentOverCardProps extends Partial<DocumentSnippet> {
    linkQuery?: Record<string, unknown> | null;
    theme?: CardTheme | LegacyCardTheme | null;
    size?: CardSize;
    ratio?: CardRatio;
    placeholder?: boolean;
    width?: number | null;
    imageLoading?: Loading;
    withoutAuthor?: boolean;
    withoutIcon?: boolean;
    withBrandIcon?: boolean;
    withoutTypeIcon?: boolean;
    withCategory?: boolean;
    withoutSurtitle?: boolean;
    withoutSerie?: boolean;
    withEpisode?: boolean;
    withoutText?: boolean;
    className?: string | null;
    onClick?: ((event: React.MouseEvent<HTMLElement>) => void) | null;
}

const DocumentOverCard = ({
    id = null,
    type = null,
    slug = null,
    linkQuery = null,
    brands = null,
    image = null,
    coverImage = null,
    categories = null,
    credits = null,
    authors = null,
    sponsors = null,
    title = null,
    surtitle = null,
    duration = null,
    topic = null,
    format = null,
    topics = null,
    formats = null,
    collection = null,
    season = null,
    episode = null,
    micromagNumber = null,
    theme: providedTheme = null,
    size = null,
    width = 120,
    ratio = null,
    placeholder = false,
    imageLoading = undefined,
    withoutAuthor = false,
    withoutIcon = false,
    withBrandIcon = false,
    withoutTypeIcon = false,
    withCategory = false,
    withoutSurtitle = false,
    withoutSerie = false,
    withEpisode = false,
    withoutText = false,
    className = null,
    onClick = null,
}: DocumentOverCardProps) => {
    const theme = normalizeCardTheme(providedTheme);
    const {
        href,
        category,
        author,
        brandHandle,
        sponsor,
        progress,
        surtitle: finalSurtitle,
    } = useDocumentCard(
        {
            id,
            type,
            slug,
            surtitle,
            brands,
            categories,
            credits,
            authors,
            sponsors,
            topic,
            format,
            topics,
            formats,
            collection,
            season,
            episode,
            micromagNumber,
        },
        {
            linkQuery,
            placeholder,
            surtitle: {
                withoutSerie,
                serieFirst: true,
                withCategory: !withCategory,
                withoutMicromagDate: true,
                withoutEpisode: !withEpisode,
            },
        },
    );

    const thumbnailRatio = getRatio(ratio, 4 / 5);

    return (
        <OverCard
            theme={theme || 'none'}
            size={size || 'default'}
            fillThumbnail={theme === 'box'}
            href={href || '#'}
            onClick={!placeholder ? onClick : null}
            thumbnail={
                <DocumentCardThumbnail
                    type={type}
                    image={image}
                    coverImage={coverImage}
                    withCover={type === 'micromag'}
                    duration={duration}
                    brand={brandHandle}
                    width={width}
                    height={width / thumbnailRatio}
                    className={styles.thumbnail}
                    iconClassName={styles.thumbnailIcon}
                    typeIconClassName={styles.typeIcon}
                    loading={imageLoading}
                    size="medium"
                    progress={progress}
                    withoutSize
                    withPlaceholder
                    withIcon={!withoutIcon}
                    withBrandIcon={withBrandIcon}
                    withoutTypeIcon={withoutTypeIcon}
                    durationClassName={styles.duration}
                />
            }
            thumbnailRatio={thumbnailRatio}
            className={classNames([
                styles.container,
                {
                    [styles.withSponsor]: sponsor !== null,
                    [styles.withoutText]: withoutText,
                },
                styles[type],
                className,
            ])}
            innerClassName={styles.inner}
            labelClassName={styles.label}
        >
            {(category !== null || placeholder) && withCategory ? (
                <Category {...category} placeholder={placeholder} className={styles.category} />
            ) : null}
            {finalSurtitle !== null && !withoutSurtitle ? (
                <Surtitle surtitle={finalSurtitle} className={styles.surtitle} />
            ) : null}
            <CardTitle className={styles.title} placeholder={placeholder} placeholderLines={2}>
                {title}
            </CardTitle>

            {sponsor !== null ? <Sponsor {...sponsor} className={styles.sponsor} /> : null}
            {sponsor === null && author !== null && !withoutAuthor ? (
                <Author {...author} withBox withoutImage className={styles.author} />
            ) : null}
        </OverCard>
    );
};

export default React.memo(DocumentOverCard);
